import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import Navbar from './Navbar';

const ACCENT = '#22d3ee';

const PLANS = [
  {
    key: 'basic',
    name: 'Basic',
    price: { monthly: 49, yearly: 490 },
    description: 'Perfetto per piccole imbarcazioni e yacht privati',
    features: [
      '1 Imbarcazione',
      'Massimo 5 membri equipaggio',
      'Funzionalità core (equipaggio, turni)',
      'Inventario base (500 articoli)',
      'Manutenzioni programmabili',
      'Documentazione digitale',
      'Supporto email',
      'App mobile iOS/Android',
    ],
    popular: false,
    color: 'rgba(240,240,248,0.1)',
  },
  {
    key: 'professional',
    name: 'Professional',
    price: { monthly: 149, yearly: 1490 },
    description: 'Soluzione completa per imbarcazioni professionali',
    features: [
      'Fino a 3 imbarcazioni',
      'Equipaggio illimitato',
      'Tutte le funzionalità core e avanzate',
      'Inventario illimitato',
      'Sistema jobs e task management',
      'Analytics e reportistica avanzata',
      'Integrazioni terze parti',
      'Compliance automatizzata',
      'Funzionalità offline complete',
      'Supporto prioritario',
      'Training onboarding incluso',
    ],
    popular: true,
    color: ACCENT,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: { monthly: null, yearly: null },
    description: 'Per flotte e operatori commerciali su larga scala',
    features: [
      'Imbarcazioni illimitate',
      'Equipaggio illimitato',
      'Tutte le funzionalità Pro',
      'API complete per integrazioni',
      'Dashboard multi-flotta',
      'Analytics predittive avanzate',
      'Customizzazioni su misura',
      'Supporto dedicato 24/7',
      'SLA garantiti (99.9% uptime)',
      'Implementation manager dedicato',
    ],
    popular: false,
    color: 'rgba(167,139,250,0.6)',
  },
];

const FAQS = [
  {
    q: 'Posso cambiare piano in qualsiasi momento?',
    a: 'Sì, puoi fare upgrade o downgrade in qualsiasi momento. Le modifiche avranno effetto nel prossimo ciclo di fatturazione.',
  },
  {
    q: 'È disponibile una prova gratuita?',
    a: 'Sì, offriamo 30 giorni di prova gratuita per tutti i piani. Non è richiesta carta di credito per iniziare.',
  },
  {
    q: "L'app funziona davvero offline?",
    a: 'Assolutamente sì. CrewBoat è progettata per funzionare completamente offline. I dati si sincronizzano automaticamente quando torni online.',
  },
  {
    q: 'Quali metodi di pagamento accettate?',
    a: 'Accettiamo tutte le principali carte di credito, bonifici bancari e PayPal. Per i piani Enterprise sono disponibili condizioni personalizzate.',
  },
  {
    q: 'È incluso il supporto tecnico?',
    a: 'Tutti i piani includono supporto tecnico. Professional e Enterprise hanno accesso prioritario e tempi di risposta più rapidi.',
  },
];

function PricingCard({ plan, billing }) {
  const [hov, setHov] = useState(false);
  const price = billing === 'monthly' ? plan.price.monthly : plan.price.yearly;
  const savings = plan.price.monthly
    ? Math.round(((plan.price.monthly * 12 - plan.price.yearly) / (plan.price.monthly * 12)) * 100)
    : null;

  return (
    <motion.div
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      animate={{ y: hov ? -6 : 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      style={{
        background: plan.popular ? `rgba(34,211,238,0.04)` : 'rgba(13,13,24,0.8)',
        border: `1px solid ${hov || plan.popular ? plan.color : 'rgba(255,255,255,0.07)'}`,
        borderRadius: 'var(--radius-lg)',
        padding: '2.25rem',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: plan.popular
          ? `0 0 40px rgba(34,211,238,0.1), 0 8px 32px rgba(0,0,0,0.4)`
          : '0 8px 32px rgba(0,0,0,0.3)',
        position: 'relative',
        transition: 'border-color 0.3s, box-shadow 0.3s',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
        background: `linear-gradient(90deg, transparent, ${plan.color}, transparent)`,
        borderRadius: '2px',
        opacity: plan.popular ? 1 : hov ? 0.7 : 0.3,
        transition: 'opacity 0.3s',
      }} />

      {plan.popular && (
        <div style={{
          position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
          fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600,
          color: '#05050a', background: ACCENT,
          borderRadius: '20px', padding: '4px 14px', whiteSpace: 'nowrap',
          letterSpacing: '0.06em',
        }}>
          PIÙ POPOLARE
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h3 style={{
          fontFamily: 'var(--font-display)', fontWeight: 700,
          fontSize: '1.4rem', color: 'var(--color-text)',
          marginBottom: '0.4rem', letterSpacing: '-0.01em',
        }}>{plan.name}</h3>
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: '0.9rem',
          color: 'var(--color-muted)', lineHeight: 1.5,
        }}>{plan.description}</p>
      </div>

      {/* Price */}
      <div style={{ marginBottom: '1.75rem' }}>
        {price ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(2.4rem, 5vw, 3rem)',
                color: plan.popular ? ACCENT : 'var(--color-text)',
                letterSpacing: '-0.03em',
              }}>€{price}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--color-muted)' }}>
                /{billing === 'monthly' ? 'mese' : 'anno'}
              </span>
            </div>
            {billing === 'yearly' && savings && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.75rem', fontWeight: 600,
                color: '#34d399', background: 'rgba(52,211,153,0.1)',
                border: '1px solid rgba(52,211,153,0.2)',
                borderRadius: '20px', padding: '3px 10px',
                display: 'inline-block', marginTop: '0.5rem',
              }}>Risparmi {savings}% all'anno</span>
            )}
          </>
        ) : (
          <span style={{
            fontFamily: 'var(--font-display)', fontWeight: 800,
            fontSize: 'clamp(2rem, 4vw, 2.4rem)',
            color: 'var(--color-accent-mid)', letterSpacing: '-0.02em',
          }}>Custom</span>
        )}
      </div>

      {/* Features */}
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 2rem', display: 'flex', flexDirection: 'column', gap: '0.65rem', flex: 1 }}>
        {plan.features.map((f, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Check size={15} color={plan.popular ? ACCENT : 'var(--color-accent-mid)'} style={{ flexShrink: 0, marginTop: '3px' }} />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: 'var(--color-text-2)', lineHeight: 1.45 }}>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Link to="/collabora" style={{ textDecoration: 'none' }}>
        <button style={{
          width: '100%', padding: '12px',
          borderRadius: '10px', border: 'none', cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem',
          background: plan.popular
            ? `linear-gradient(135deg, ${ACCENT}, #0ea5e9)`
            : 'rgba(255,255,255,0.06)',
          color: plan.popular ? '#05050a' : 'var(--color-text)',
          border: plan.popular ? 'none' : '1px solid rgba(255,255,255,0.1)',
          boxShadow: plan.popular ? `0 0 24px rgba(34,211,238,0.25)` : 'none',
          transition: 'opacity 0.2s, transform 0.2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          {plan.key === 'enterprise' ? 'Contatta il team' : 'Inizia ora'}
        </button>
      </Link>
    </motion.div>
  );
}

export default function CrewBoatPricingPage() {
  const [billing, setBilling] = useState('monthly');

  return (
    <>
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: 'var(--color-bg)', minHeight: '100vh' }}
      >
        {/* ── Hero ── */}
        <section style={{
          paddingTop: '68px',
          background: 'linear-gradient(180deg, rgba(34,211,238,0.06) 0%, transparent 100%)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <div className="container" style={{ padding: '4rem 1.75rem 3.5rem', textAlign: 'center' }}>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.82rem',
              color: ACCENT, letterSpacing: '0.12em',
              textTransform: 'uppercase', display: 'block', marginBottom: '1rem',
            }}>{'// crewboat · pricing'}</span>
            <h1 style={{
              fontFamily: 'var(--font-display)', fontWeight: 800,
              fontSize: 'clamp(2.4rem, 6vw, 3.6rem)',
              letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '1rem',
            }}>
              Piani{' '}
              <span style={{ color: ACCENT }}>semplici</span>
              ,{' '}risultati concreti
            </h1>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 'clamp(1rem, 2vw, 1.2rem)',
              color: 'var(--color-text-2)', maxWidth: '540px', margin: '0 auto 2.5rem', lineHeight: 1.7,
            }}>
              30 giorni di prova gratuita su tutti i piani. Nessuna carta di credito richiesta.
            </p>

            {/* Badge */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2.5rem' }}>
              {['📱 iOS & Android', '🌐 Funziona Offline', '⚓ Compliance Marittima'].map(b => (
                <span key={b} style={{
                  fontFamily: 'var(--font-mono)', fontSize: '0.78rem',
                  color: 'var(--color-text-2)',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
                  borderRadius: '8px', padding: '6px 14px',
                }}>{b}</span>
              ))}
            </div>

            {/* Billing toggle */}
            <div style={{
              display: 'inline-flex',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
              borderRadius: '12px', padding: '4px',
            }}>
              {['monthly', 'yearly'].map(p => (
                <button key={p} onClick={() => setBilling(p)} style={{
                  padding: '8px 24px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '0.9rem',
                  background: billing === p ? 'rgba(255,255,255,0.08)' : 'transparent',
                  color: billing === p ? 'var(--color-text)' : 'var(--color-muted)',
                  transition: 'all 0.2s', position: 'relative',
                }}>
                  {p === 'monthly' ? 'Mensile' : 'Annuale'}
                  {p === 'yearly' && (
                    <span style={{
                      position: 'absolute', top: '-10px', right: '-8px',
                      fontFamily: 'var(--font-mono)', fontSize: '0.65rem', fontWeight: 700,
                      color: '#05050a', background: '#34d399',
                      borderRadius: '20px', padding: '2px 6px',
                    }}>-17%</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── Piani ── */}
        <section className="section">
          <div className="container">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem',
              alignItems: 'start',
            }}>
              {PLANS.map(plan => (
                <PricingCard key={plan.key} plan={plan} billing={billing} />
              ))}
            </div>
          </div>
        </section>

        {/* ── Perché CrewBoat ── */}
        <section style={{ borderTop: '1px solid var(--color-border)', padding: '5rem 0' }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: ACCENT,
                letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '0.75rem',
              }}>{'// perché crewboat'}</span>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                letterSpacing: '-0.03em', margin: 0,
              }}>Costruito per il mare,<br />non per l'ufficio</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              {[
                { icon: '⚓', title: 'Compliance Integrata', desc: 'Conformità automatica a SOLAS, MARPOL, STCW e normative internazionali.' },
                { icon: '📱', title: 'Mobile-First', desc: "App nativa iOS e Android ottimizzata per l'ambiente marittimo." },
                { icon: '🌐', title: 'Funziona Offline', desc: 'Piena operatività senza connessione, con sync automatica al rientro.' },
                { icon: '🔒', title: 'Sicurezza Avanzata', desc: 'Crittografia end-to-end e conformità GDPR per proteggere i tuoi dati.' },
              ].map(({ icon, title, desc }) => (
                <div key={title} style={{
                  background: 'rgba(13,13,24,0.8)', border: '1px solid var(--color-border)',
                  backdropFilter: 'blur(16px)', borderRadius: 'var(--radius-lg)',
                  padding: '1.75rem',
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>{icon}</div>
                  <h3 style={{
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05rem',
                    color: 'var(--color-text)', marginBottom: '0.5rem',
                  }}>{title}</h3>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: 'var(--color-muted)', lineHeight: 1.6, margin: 0 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section style={{ borderTop: '1px solid var(--color-border)', padding: '5rem 0' }}>
          <div className="container" style={{ maxWidth: '720px' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: ACCENT,
                letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '0.75rem',
              }}>{'// faq'}</span>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(1.8rem, 4vw, 2.4rem)',
                letterSpacing: '-0.03em', margin: 0,
              }}>Domande frequenti</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {FAQS.map(({ q, a }) => (
                <div key={q} style={{
                  background: 'rgba(13,13,24,0.8)', border: '1px solid var(--color-border)',
                  backdropFilter: 'blur(16px)', borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem 1.75rem',
                }}>
                  <h4 style={{
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1rem',
                    color: 'var(--color-text)', marginBottom: '0.6rem',
                  }}>{q}</h4>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem', color: 'var(--color-muted)', lineHeight: 1.65, margin: 0 }}>{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA finale ── */}
        <section style={{ borderTop: '1px solid var(--color-border)', padding: '5rem 0' }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: ACCENT,
              letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '1rem',
            }}>{'// inizia adesso'}</span>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontWeight: 800,
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              letterSpacing: '-0.03em', marginBottom: '1rem',
            }}>
              Pronto a portare la tua flotta<br />
              <span style={{ color: ACCENT }}>nel futuro digitale?</span>
            </h2>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: '1.1rem',
              color: 'var(--color-text-2)', marginBottom: '2.5rem', lineHeight: 1.7,
            }}>
              Contattaci per scegliere il piano giusto o per una demo personalizzata.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/collabora" className="btn-primary" style={{ textDecoration: 'none' }}>
                Richiedi una demo
              </Link>
              <Link to="/crewboat" className="btn-secondary" style={{ textDecoration: 'none' }}>
                ← Torna a CrewBoat
              </Link>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{ borderTop: '1px solid var(--color-border)', padding: '2rem 0' }}>
          <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem' }}>VMDProject</span>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <Link to="/crewboat/policy" style={{ fontSize: '0.9rem', color: 'var(--color-muted)', textDecoration: 'none' }}>Privacy</Link>
              <Link to="/crewboat/terms" style={{ fontSize: '0.9rem', color: 'var(--color-muted)', textDecoration: 'none' }}>Termini</Link>
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--color-muted)' }}>
              © {new Date().getFullYear()} VMDProject
            </span>
          </div>
        </footer>
      </motion.main>
    </>
  );
}
